import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Plus,
  Trash2,
  Edit3,
  Save,
  X,
  HelpCircle,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TYPE_LABELS = {
  scale: "Escala 0–4 (avaliativa)",
  currency: "Valor R$ (financeira)",
  number: "Número (quantidade)",
  single_choice: "Múltipla escolha",
  text: "Texto livre",
};

const emptyQ = {
  section_id: "",
  text: "",
  weight: 1,
  risk: "",
  recommended_action: "",
  order: 0,
  question_type: "scale",
  options: [],
};

const AdminQuestions = () => {
  const { user } = useAuth();
  const canEdit = user?.role === "admin";
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyQ);

  const load = async () => {
    setLoading(true);
    try {
      const [s, q] = await Promise.all([
        api.get("/sections"),
        api.get("/questions"),
      ]);
      setSections(s.data);
      setQuestions(q.data);
      if (!activeSection && s.data.length > 0) setActiveSection(s.data[0].id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const sectionQuestions = useMemo(
    () => questions.filter((q) => q.section_id === activeSection),
    [questions, activeSection]
  );

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyQ, section_id: activeSection || "", order: sectionQuestions.length, options: [] });
    setOpen(true);
  };
  const openEdit = (q) => {
    setEditing(q);
    setForm({
      section_id: q.section_id,
      text: q.text,
      weight: q.weight,
      risk: q.risk || "",
      recommended_action: q.recommended_action || "",
      order: q.order,
      question_type: q.question_type || "scale",
      options: Array.isArray(q.options) ? [...q.options] : [],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.section_id) {
      toast.error("Selecione a sessão.");
      return;
    }
    if (!form.text.trim()) {
      toast.error("Informe o texto da pergunta.");
      return;
    }
    // Validate options for single_choice
    let cleanOptions = null;
    if (form.question_type === "single_choice") {
      cleanOptions = (form.options || []).map((o) => o.trim()).filter(Boolean);
      if (cleanOptions.length < 2) {
        toast.error("Adicione ao menos 2 opções para perguntas de múltipla escolha.");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        section_id: form.section_id,
        text: form.text,
        risk: form.risk,
        recommended_action: form.recommended_action,
        question_type: form.question_type,
        weight: Number(form.weight) || 1,
        order: Number(form.order) || 0,
        options: cleanOptions,
      };
      if (editing) {
        await api.put(`/questions/${editing.id}`, payload);
        toast.success("Pergunta atualizada.");
      } else {
        await api.post("/questions", payload);
        toast.success("Pergunta criada.");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (q) => {
    if (!window.confirm("Excluir esta pergunta?")) return;
    try {
      await api.delete(`/questions/${q.id}`);
      toast.success("Pergunta excluída.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3]">
            Configuração
          </div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <HelpCircle className="text-[#B9262B]" /> Perguntas
          </h1>
          <p className="text-[#9aa3ad] mt-1">
            Defina perguntas por sessão, com peso, risco e ação recomendada.
          </p>
        </div>
        <Button
          data-testid="new-question-btn"
          onClick={openNew}
          disabled={!sections.length || !canEdit}
          className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
          title={!canEdit ? "Somente administradores podem criar perguntas" : ""}
        >
          <Plus size={16} className="mr-2" /> Nova pergunta
        </Button>
      </div>

      {/* Section tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {sections.map((s) => (
          <button
            key={s.id}
            onClick={() => setActiveSection(s.id)}
            data-testid={`section-tab-${s.id}`}
            className={[
              "px-4 py-2 rounded-lg text-sm transition-all border",
              activeSection === s.id
                ? "bg-[#1C1F26] text-white border-[#B9262B]"
                : "bg-[#0e1015] text-[#9aa3ad] border-[#3A3F47] hover:border-[#6B8DB3]/60",
            ].join(" ")}
          >
            {s.name}
            <span className="ml-2 text-xs font-mono text-[#6B8DB3]">
              ({questions.filter((q) => q.section_id === s.id).length})
            </span>
          </button>
        ))}
      </div>

      <div className="border border-[#3A3F47] rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#6B8DB3]">
            <Loader2 className="animate-spin inline mr-2" /> Carregando...
          </div>
        ) : sectionQuestions.length === 0 ? (
          <div className="p-10 text-center text-[#6B8DB3]">
            Nenhuma pergunta nesta sessão ainda.
          </div>
        ) : (
          <div className="divide-y divide-[#1C1F26]">
            {sectionQuestions.map((q, i) => (
              <div
                key={q.id}
                className="p-5 flex flex-col md:flex-row md:items-center gap-4 hover:bg-[#1C1F26]/40"
                data-testid={`question-row-${q.id}`}
              >
                <div className="font-mono text-[#6B8DB3] text-xs w-8">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-medium">{q.text}</div>
                  <div className="text-xs text-[#6B8DB3] mt-1 flex flex-wrap gap-3">
                    <span>Tipo: <span className="text-[#22B673]">{TYPE_LABELS[q.question_type] || q.question_type}</span></span>
                    <span>Peso: <span className="text-[#F5A623]">{q.weight}</span></span>
                    {q.options?.length > 0 && (
                      <span>Opções: <span className="text-[#9aa3ad]">{q.options.length}</span></span>
                    )}
                    {q.risk && (
                      <span>Risco: <span className="text-[#9aa3ad]">{q.risk.slice(0, 60)}{q.risk.length > 60 ? "…" : ""}</span></span>
                    )}
                  </div>
                </div>
                {canEdit ? (
                  <div className="inline-flex gap-2">
                    <button
                      onClick={() => openEdit(q)}
                      data-testid={`edit-question-${q.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs bg-[#1C1F26] hover:bg-[#252932] border border-[#3A3F47]"
                    >
                      <Edit3 size={14} /> Editar
                    </button>
                    <button
                      onClick={() => remove(q)}
                      data-testid={`delete-question-${q.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs bg-[#B9262B]/15 hover:bg-[#B9262B]/30 text-[#B9262B] border border-[#B9262B]/40"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <span className="text-[10px] uppercase tracking-wider text-[#6B8DB3]">
                    Somente leitura
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1C1F26] border-[#3A3F47] text-white max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar pergunta" : "Nova pergunta"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">
                Sessão
              </Label>
              <Select
                value={form.section_id}
                onValueChange={(v) => setForm({ ...form, section_id: v })}
              >
                <SelectTrigger
                  className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
                  data-testid="question-section-select"
                >
                  <SelectValue placeholder="Selecione a sessão" />
                </SelectTrigger>
                <SelectContent className="bg-[#1C1F26] border-[#3A3F47] text-white">
                  {sections.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">
                Texto da pergunta
              </Label>
              <Textarea
                rows={3}
                data-testid="question-text-input"
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">
                  Tipo de pergunta
                </Label>
                <Select
                  value={form.question_type}
                  onValueChange={(v) => setForm({ ...form, question_type: v })}
                >
                  <SelectTrigger data-testid="question-type-select" className="bg-[#0e1015] border-[#3A3F47] text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1F26] border-[#3A3F47] text-white">
                    <SelectItem value="scale">Escala 0–4 (avaliativa)</SelectItem>
                    <SelectItem value="currency">Valor R$ (financeira)</SelectItem>
                    <SelectItem value="number">Número (quantidade)</SelectItem>
                    <SelectItem value="single_choice">Múltipla escolha (opções customizadas)</SelectItem>
                    <SelectItem value="text">Texto livre</SelectItem>
                  </SelectContent>
                </Select>
                {(form.question_type === "single_choice" || form.question_type === "text") && (
                  <p className="text-[10px] text-[#6B8DB3] mt-1 leading-relaxed">
                    Perguntas qualitativas não entram no cálculo de maturidade — servem para coletar contexto adicional.
                  </p>
                )}
              </div>
              <div>
                <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">
                  Peso (1–2)
                </Label>
                <Select
                  value={String(form.weight)}
                  onValueChange={(v) => setForm({ ...form, weight: Number(v) })}
                >
                  <SelectTrigger className="bg-[#0e1015] border-[#3A3F47] text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1F26] border-[#3A3F47] text-white">
                    <SelectItem value="1">1 — Normal</SelectItem>
                    <SelectItem value="2">2 — Crítica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.question_type === "single_choice" && (
              <div data-testid="options-manager" className="p-4 rounded-lg border border-[#3A3F47] bg-[#0e1015]">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">
                    Opções de resposta ({form.options?.length || 0})
                  </Label>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, options: [...(form.options || []), ""] })}
                    data-testid="add-option-btn"
                    className="text-xs text-[#22B673] hover:text-[#1a9b60] font-semibold flex items-center gap-1"
                  >
                    <Plus size={12} /> Adicionar opção
                  </button>
                </div>
                {(form.options || []).length === 0 ? (
                  <div className="text-xs text-[#6B8DB3] text-center py-4">
                    Adicione pelo menos uma opção para que o respondente possa escolher.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(form.options || []).map((opt, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="font-mono text-[#6B8DB3] text-xs w-6">
                          {String(idx + 1).padStart(2, "0")}
                        </div>
                        <Input
                          value={opt}
                          onChange={(e) => {
                            const next = [...form.options];
                            next[idx] = e.target.value;
                            setForm({ ...form, options: next });
                          }}
                          placeholder={`Opção ${idx + 1}`}
                          data-testid={`option-input-${idx}`}
                          className="bg-[#1C1F26] border-[#3A3F47] text-white h-9 text-sm flex-1"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const next = form.options.filter((_, i) => i !== idx);
                            setForm({ ...form, options: next });
                          }}
                          data-testid={`remove-option-${idx}`}
                          className="w-9 h-9 inline-flex items-center justify-center rounded text-[#6B8DB3] hover:text-[#B9262B] hover:bg-[#B9262B]/10 border border-[#3A3F47]"
                          title="Remover opção"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div>
              <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">
                Ordem
              </Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: e.target.value })}
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">
                Risco associado
              </Label>
              <Textarea
                rows={2}
                value={form.risk}
                onChange={(e) => setForm({ ...form, risk: e.target.value })}
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-[#9aa3ad] text-xs uppercase tracking-wider">
                Ação recomendada
              </Label>
              <Textarea
                rows={2}
                value={form.recommended_action}
                onChange={(e) =>
                  setForm({ ...form, recommended_action: e.target.value })
                }
                className="bg-[#0e1015] border-[#3A3F47] text-white mt-1"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-[#3A3F47] text-white hover:bg-[#0e1015]"
            >
              <X size={14} className="mr-2" /> Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="save-question-btn"
              className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
            >
              {saving ? (
                <Loader2 className="animate-spin" />
              ) : (
                <>
                  <Save size={14} className="mr-2" /> Salvar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuestions;
